import type { Store, StoreUpdate } from '../api/types'
import { defineStore } from 'pinia'
import { computed, readonly, ref, watch } from 'vue'
import { isAuthenticated } from '@/shared/lib'
import { getCookie, setCookie } from '@/shared/lib/utils'
import {
  storeApi,
  useFetchMyStoresQuery,
  useFetchStoreDetailQuery,
} from '../api/storeApi'

export const useStoresStore = defineStore('stores', () => {
  // === STORES QUERY INTEGRATION ===
  const storesQuery = useFetchMyStoresQuery()

  // === CURRENT STORE STATE ===
  const currentStoreId = ref<string | null>(getCookie('storeId'))

  // === COMPUTED PROPERTIES ===
  const stores = computed<Store[]>(() => storesQuery.data.value || [])
  const isLoading = computed(() => storesQuery.isLoading.value)
  const error = computed(() => storesQuery.error.value?.message || null)
  const hasStores = computed(() => stores.value.length > 0)

  // === STORE DETAIL QUERY INTEGRATION ===
  const storeDetailQuery = useFetchStoreDetailQuery(
    computed(
      () =>
        isAuthenticated()
        && !isLoading.value
        && hasStores.value
        && !!currentStoreId.value,
    ),
  )

  // Store detail computed properties
  const storeDetail = computed<Store | null>(
    () => storeDetailQuery.data.value || null,
  )
  const isStoreDetailLoading = computed(() => storeDetailQuery.isLoading.value)
  const storeDetailError = computed(
    () => storeDetailQuery.error.value?.message || null,
  )

  const currentStore = computed<Store | null>(() => {
    if (!currentStoreId.value) 
      return null
    return (
      stores.value.find(
        store => store.id.toString() === currentStoreId.value,
      ) || null
    )
  })

  // === COOKIE MANAGEMENT ===
  // Watch for stores data changes and set first store ID to cookie if needed
  // This is a side effect, but it's tightly coupled to store data
  watch(
    stores,
    (newStores) => {
      if (newStores.length > 0) {
        const cookieStoreId = getCookie('storeId')
        if (!cookieStoreId) {
          // Set the first store ID to cookie
          const firstStoreId = newStores[0].id.toString()
          currentStoreId.value = firstStoreId
          setCookie('storeId', firstStoreId)
        }
        else {
          currentStoreId.value = cookieStoreId
        }
      }
    },
    { immediate: true },
  )

  // === ACTIONS ===
  const updateStore = async (storeData: StoreUpdate) => {
    try {
      const updatedStore = await storeApi.updateStore(storeData)
      // Refetch stores to get updated data
      await storesQuery.refetch()
      return updatedStore
    }
    catch (error) {
      console.error('Failed to update store:', error)
      throw error
    }
  }

  const updateAutoReviewReply = async (autoReviewReply: boolean) => {
    return updateStore({ auto_review_reply: autoReviewReply })
  }

  // === STORE DETAIL ACTIONS ===
  const fetchStoreDetail = async () => {
    try {
      // Skip if unauthenticated or no active store — backend 500s without x-store-id
      if (!isAuthenticated() || !currentStoreId.value) {
        return null
      }
      const result = await storeDetailQuery.refetch()
      return result.data
    }
    catch (error) {
      console.error('Failed to fetch store detail:', error)
      throw error
    }
  }

  // === STORES ACTIONS ===
  const fetchStores = async () => {
    try {
      // Only fetch if user is authenticated
      if (!isAuthenticated()) {
        return null
      }
      const result = await storesQuery.refetch()
      return result.data
    }
    catch (error) {
      console.error('Failed to fetch stores:', error)
      throw error
    }
  }

  // === RESET ACTIONS ===
  const resetStore = () => {
    // Reset current store ID
    currentStoreId.value = null
  }

  // === PUBLIC API ===
  return {
    // Data
    stores: readonly(stores),
    isLoading: readonly(isLoading),
    error: readonly(error),
    hasStores: readonly(hasStores),
    currentStore: readonly(currentStore),
    currentStoreId: readonly(currentStoreId),

    // Store detail data
    storeDetail: readonly(storeDetail),
    isStoreDetailLoading: readonly(isStoreDetailLoading),
    storeDetailError: readonly(storeDetailError),

    // Actions
    refetch: storesQuery.refetch,
    updateStore,
    updateAutoReviewReply,
    fetchStoreDetail,
    fetchStores,
    resetStore,
  }
})
